import axiosInstance from '../../../services/axiosInstance';
import {
  AlcoholDetail,
  AlcoholFilter,
  AlcoholHistoryEntry,
  AlcoholImportPreview,
  AlcoholList,
  AlcoholPrompts,
  AlcoholProducer,
  AlcoholSettings,
  AlcoholSuggestion,
} from './alcoholTypes';

const base = '/api/nyhetslanseringar/alcohol-info';
const item = (id: string) => `${base}/items/${encodeURIComponent(id)}`;
const producer = (id: string) => `${base}/producers/${encodeURIComponent(id)}`;

const params = (filter: AlcoholFilter) => Object.fromEntries(
  Object.entries(filter).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
);

const upload = (file: File, mode?: 'all' | 'missing') => {
  const form = new FormData();
  if (mode) form.append('mode', mode);
  form.append('file', file);
  return form;
};

export const alcoholApi = {
  async list(filter: AlcoholFilter): Promise<AlcoholList> {
    const response = await axiosInstance.get(base, { params: params(filter) });
    return response.data;
  },
  async item(id: string): Promise<AlcoholDetail> {
    const response = await axiosInstance.get(item(id));
    return response.data;
  },
  async history(id: string): Promise<AlcoholHistoryEntry[]> {
    const response = await axiosInstance.get(`${item(id)}/history`);
    return response.data.history;
  },
  async create(input: { commandId: string; patch: Record<string, unknown>; allowDuplicate?: boolean }): Promise<AlcoholDetail> {
    const response = await axiosInstance.post(`${base}/items`, input);
    return response.data;
  },
  async command(id: string, command: Record<string, unknown>): Promise<AlcoholDetail> {
    const response = await axiosInstance.post(`${item(id)}/commands`, command);
    return response.data;
  },
  async generate(id: string, field: 'description' | 'shortDescription', text: string): Promise<AlcoholSuggestion> {
    const response = await axiosInstance.post(`${item(id)}/generate`, { field, text });
    return response.data;
  },
  async producers(): Promise<AlcoholProducer[]> {
    const response = await axiosInstance.get(`${base}/producers`);
    return response.data.producers;
  },
  async producerCommand(id: string, command: { commandId: string; expectedVersion: number; wineryText: string }): Promise<AlcoholProducer> {
    const response = await axiosInstance.post(`${producer(id)}/commands`, { type: 'updateProducer', ...command });
    return response.data;
  },
  async producerHistory(id: string): Promise<AlcoholHistoryEntry[]> {
    const response = await axiosInstance.get(`${producer(id)}/history`);
    return response.data.history;
  },
  async generateWinery(id: string, text: string): Promise<AlcoholSuggestion> {
    const response = await axiosInstance.post(`${producer(id)}/generate`, { text });
    return response.data;
  },
  async exportCsv(filter: AlcoholFilter): Promise<Blob> {
    const response = await axiosInstance.get(`${base}/export.csv`, { params: params(filter), responseType: 'blob' });
    return response.data;
  },
  async settings(): Promise<AlcoholSettings> {
    const response = await axiosInstance.get(`${base}/settings`);
    return response.data;
  },
  async saveSettings(input: { prompts: AlcoholPrompts; expectedVersion: number; commandId: string }): Promise<AlcoholSettings> {
    const response = await axiosInstance.put(`${base}/settings`, input);
    return response.data;
  },
  async settingsHistory(): Promise<AlcoholHistoryEntry[]> {
    const response = await axiosInstance.get(`${base}/settings/history`);
    return response.data.history;
  },
  async previewImport(file: File): Promise<AlcoholImportPreview> {
    const response = await axiosInstance.post(`${base}/import/preview`, upload(file), { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  async runImport(file: File, mode: 'all' | 'missing'): Promise<{ imported: number; skipped: number; producers: number }> {
    const response = await axiosInstance.post(`${base}/import`, upload(file, mode), { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
};
